import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const UploadHistory = ({ history, onRefresh }) => {
  const navigate = useNavigate();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  const getStatusConfig = (status) => {
    const configs = {
      'completed': {
        icon: 'CheckCircle2',
        color: 'text-success',
        bgColor: 'bg-success/10',
        label: 'Concluído'
      },
      'processing': {
        icon: 'Loader2',
        color: 'text-primary',
        bgColor: 'bg-primary/10',
        label: 'Processando',
        animate: true
      },
      'failed': {
        icon: 'XCircle',
        color: 'text-error',
        bgColor: 'bg-error/10',
        label: 'Falhou'
      },
      'pending': {
        icon: 'Clock',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        label: 'Pendente'
      }
    };
    return configs?.[status] || configs?.pending;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  const handleClearHistory = async () => {
    try {
      setClearing(true);
      
      // Delete all CSV import records
      const { error: importError } = await supabase
        ?.from('csv_imports')
        ?.delete()
        ?.neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all
      
      if (importError) throw importError;
      
      // Delete all products from CSV imports
      const { error: productsError } = await supabase
        ?.from('products')
        ?.delete()
        ?.not('csv_import_id', 'is', null); // Delete only CSV imported products
      
      if (productsError) throw productsError;
      
      setShowClearConfirm(false);
      onRefresh?.();
      window.location?.reload();
    } catch (error) {
      console.error('Error clearing history:', error);
      alert('Erro ao limpar histórico: ' + error?.message);
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
            <Icon name="History" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h4 className="font-medium text-foreground">Histórico de Importações</h4>
            <p className="caption text-muted-foreground">
              {history?.length} {history?.length === 1 ? 'importação' : 'importações'} recentes
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {history?.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => setShowClearConfirm(true)}
            >
              Limpar
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => window.location?.reload()}
          >
            Atualizar
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {history?.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
                <Icon name="FileX" size={24} className="text-muted-foreground" />
              </div>
            </div>
            <p className="text-muted-foreground mb-2">Nenhuma importação encontrada</p>
            <p className="caption text-muted-foreground">
              Faça upload do seu primeiro arquivo CSV para começar
            </p>
          </div>
        ) : (
          history?.map((item) => {
            const statusConfig = getStatusConfig(item?.status);

            return (
              <div
                key={item?.id}
                className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 rounded-lg border border-border bg-muted/30 hover:bg-muted/50 transition-smooth"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-md ${statusConfig?.bgColor} flex-shrink-0`}>
                    <Icon
                      name={statusConfig?.icon}
                      size={18}
                      className={`${statusConfig?.color} ${statusConfig?.animate ? 'animate-spin' : ''}`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground truncate">
                        {item?.fileName}
                      </p>
                      <span className={`caption px-2 py-0.5 rounded-full ${statusConfig?.bgColor} ${statusConfig?.color} whitespace-nowrap`}>
                        {statusConfig?.label}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 caption text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Calendar" size={14} />
                        {item?.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Clock" size={14} />
                        {item?.time}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="HardDrive" size={14} />
                        {formatFileSize(item?.fileSize)}
                      </span>
                      {item?.productsCount && (
                        <span className="flex items-center gap-1">
                          <Icon name="Package" size={14} />
                          {item?.productsCount} produtos
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 lg:flex-shrink-0">
                  {item?.status === 'completed' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Eye"
                        iconPosition="left"
                        onClick={() => navigate('/product-catalog-manager')}
                      >
                        Ver Catálogo
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        onClick={() => {}}
                      >
                        <span className="sr-only">Baixar</span>
                      </Button>
                    </>
                  )}

                  {item?.status === 'failed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RotateCcw"
                      iconPosition="left"
                      onClick={() => {}}
                    >
                      Tentar Novamente
                    </Button>
                  )}

                  {item?.status === 'processing' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => {}}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <>
          <div 
            className="fixed inset-0 z-[190] bg-black/50 backdrop-blur-sm"
            onClick={() => !clearing && setShowClearConfirm(false)}
          />
          <div className="fixed inset-4 lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-full lg:max-w-md z-[200] bg-card rounded-lg shadow-brand-xl overflow-hidden">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Limpar Histórico</h3>
                  <p className="caption text-muted-foreground">Esta ação não pode ser desfeita</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-6">
                Tem certeza que deseja limpar todo o histórico de importações? Todos os produtos importados via CSV serão removidos permanentemente.
              </p>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowClearConfirm(false)}
                  disabled={clearing}
                  fullWidth
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  iconName={clearing ? "Loader" : "Trash2"}
                  iconPosition="left"
                  onClick={handleClearHistory}
                  disabled={clearing}
                  fullWidth
                >
                  {clearing ? 'Limpando...' : 'Limpar Tudo'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UploadHistory;